/**
 * Says whether something rewrote the server's markup before Vue got to hydrate it - in practice a
 * browser translator (Chrome, Edge, the Google Translate widget), which replaces every text node
 * with its translation and wraps it in `<font>`.
 *
 * Hydrating over that is hopeless: every text node disagrees, and the mismatches reach Sentry as if
 * we had rendered the wrong thing. The verdict is the same as for a degraded render - client-render
 * over the markup, which is what UAT and PROD do anyway. The translator re-translates whatever we
 * put there, so nothing is lost but the hydration.
 *
 * Timing: the mismatches we see in Sentry name the injected `<font>` nodes, so the rewrite is
 * already in the DOM when hydration runs. A translation that lands *after* hydration is invisible
 * to this check - and to Vue, which only compares while hydrating.
 */

const HTML_NS = 'http://www.w3.org/1999/xhtml';

/** What Google Translate and Chrome's built-in translator put on `<html>`. */
const TRANSLATED_CLASSES = ['translated-ltr', 'translated-rtl'];

export const translatedDomReason = (
  root?: Element | null,
  // The island's own document, not the ambient one: in the page editor the site renders inside a
  // same-origin iframe, and it is that document a translator marks.
  documentElement: Element | null | undefined = root?.ownerDocument?.documentElement,
): string | undefined => {
  // SVG font assets have <font> elements of their own, so the namespace has to be checked.
  const wrapped = Array.from(root?.querySelectorAll('font') ?? []).some(
    (element) => element.namespaceURI === HTML_NS,
  );
  if (wrapped) {
    return 'translated-font';
  }

  if (TRANSLATED_CLASSES.some((name) => documentElement?.classList.contains(name))) {
    return 'translated-html';
  }

  return undefined;
};
